import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Switch,
  Paper,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Fade,
  Alert,
  Snackbar,
  Container,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockIcon from "@mui/icons-material/Lock";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import { useQueryClient } from "react-query";
import { GlassButtonLoading } from "../../themes/UiElements";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const COLUMN_TYPES = [
  "Text",
  "Number",
  "Date",
  "Email",
  "URL",
  "Boolean",
  "Dropdown",
];
const MANDATORY_NAMES = ["phoneNumber", "message"];

let idCounter = 100;

const toEditableColumns = (apiColumns) =>
  apiColumns.map((col) => ({
    id: `col-${++idCounter}`,
    name: col.name,
    type: col.type,
    required: col.required,
    locked: MANDATORY_NAMES.includes(col.name),
  }));

export default function SheetSchemaEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useApiEp(`/user/sheet-schemas/${id}`);

  const [sheetName, setSheetName] = useState("");
  const [columns, setColumns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [dragOver, setDragOver] = useState(null);
  const [dragging, setDragging] = useState(null);

  // Populate form once data arrives
  useEffect(() => {
    if (data?.data) {
      setSheetName(data.data.sheetName);
      setColumns(toEditableColumns(data.data.columns));
    }
  }, [data]);

  const addColumn = () =>
    setColumns((prev) => [
      ...prev,
      {
        id: `col-${++idCounter}`,
        name: "",
        type: "Text",
        required: false,
        locked: false,
      },
    ]);

  const updateColumn = (colId, field, value) =>
    setColumns((prev) =>
      prev.map((c) => (c.id === colId ? { ...c, [field]: value } : c))
    );

  const removeColumn = (colId) =>
    setColumns((prev) => prev.filter((c) => c.id !== colId));

  const handleDragStart = (colId) => setDragging(colId);
  const handleDragOver = (e, colId) => {
    e.preventDefault();
    setDragOver(colId);
  };
  const handleDrop = (targetId) => {
    if (!dragging || dragging === targetId) return;
    const fromIdx = columns.findIndex((c) => c.id === dragging);
    const toIdx = columns.findIndex((c) => c.id === targetId);
    if (columns[fromIdx].locked || columns[toIdx].locked) return;
    const next = [...columns];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setColumns(next);
    setDragging(null);
    setDragOver(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_ENDPOINT}/user/sheet-schemas/${id}`,
        {
          sheetName,
          columns: columns.map(({ name, type, required }) => ({
            name,
            type,
            required,
          })),
        },
        getHeader()
      );
      // Invalidate both list and detail queries
      queryClient.invalidateQueries([`${API_ENDPOINT}/user/sheet-schemas`]);
      queryClient.invalidateQueries([
        `${API_ENDPOINT}/user/sheet-schemas/${id}`,
      ]);
      setSnackbar({
        open: true,
        msg: "Schema updated successfully",
        severity: "success",
      });
      setTimeout(() => navigate("/sheet-schemas"), 1200);
    } catch (err) {
      const msg = err?.response?.data?.message ?? "Failed to save changes";
      setSnackbar({ open: true, msg, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const allValid = columns.every((c) => c.name.trim() !== "");

  if (isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Container>
          <Skeleton variant="rounded" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" height={300} />
        </Container>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4 }}>
        <Container>
          <Alert severity="error">
            Failed to load schema. It may not exist.
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/sheet-schemas")}
            sx={{ mt: 2 }}
          >
            Back to list
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        p: { xs: 2, md: 4 },
        backgroundImage:
          "radial-gradient(ellipse at 20% 0%, rgba(74,222,128,0.06) 0%, transparent 60%)",
      }}
    >
      <Container>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <IconButton
            onClick={() => navigate("/sheet-schemas")}
            size="small"
            sx={{ color: "text.secondary", mr: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "rgba(74,222,128,0.12)",
              border: "1px solid rgba(74,222,128,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TableChartIcon sx={{ color: "primary.main", fontSize: 20 }} />
          </Box>
          <Typography
            variant="h4"
            sx={{ color: "primary.main", letterSpacing: "-0.5px" }}
          >
            Edit Schema
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 4, pl: "96px" }}
        >
          Modify your sheet schema. Drag to reorder custom columns.
        </Typography>

        {/* Sheet Name */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              letterSpacing: 1,
              textTransform: "uppercase",
              mb: 1.5,
              display: "block",
            }}
          >
            Sheet Name
          </Typography>
          <TextField
            fullWidth
            value={sheetName}
            onChange={(e) => setSheetName(e.target.value)}
            placeholder="Enter sheet name..."
            variant="outlined"
            size="small"
            inputProps={{
              style: {
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
              },
            }}
          />
        </Paper>

        {/* Columns */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              Columns · {columns.length} total
            </Typography>
            <Chip
              label="2 mandatory"
              size="small"
              icon={<LockIcon sx={{ fontSize: "13px !important" }} />}
              sx={{
                bgcolor: "rgba(74,222,128,0.1)",
                color: "primary.main",
                border: "1px solid rgba(74,222,128,0.2)",
                fontSize: 11,
              }}
            />
          </Box>

          {/* Column header row */}
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, px: 1 }}>
            <Box sx={{ width: 28 }} />
            {["Column Name", "Type", "Required", ""].map((label, i) => (
              <Typography
                key={i}
                variant="caption"
                sx={{
                  flex: i === 0 ? 2 : i === 1 ? 1 : undefined,
                  width: i === 2 ? 80 : i === 3 ? 36 : undefined,
                  color: "text.secondary",
                  letterSpacing: "0.8px",
                  textTransform: "uppercase",
                  fontSize: 10,
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          <Divider sx={{ borderColor: "rgba(74,222,128,0.25)", mb: 1.5 }} />

          {columns.map((col) => (
            <Fade in key={col.id} timeout={200}>
              <Box
                draggable={!col.locked}
                onDragStart={() => handleDragStart(col.id)}
                onDragOver={(e) => handleDragOver(e, col.id)}
                onDrop={() => handleDrop(col.id)}
                onDragEnd={() => {
                  setDragging(null);
                  setDragOver(null);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  p: 1,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: col.locked
                    ? "rgba(74,222,128,0.18)"
                    : dragOver === col.id
                    ? "rgba(74,222,128,0.4)"
                    : "transparent",
                  bgcolor: col.locked
                    ? "rgba(74,222,128,0.04)"
                    : dragging === col.id
                    ? "rgba(74,222,128,0.06)"
                    : "transparent",
                  transition: "all 0.15s ease",
                  cursor: col.locked ? "default" : "grab",
                }}
              >
                <Box
                  sx={{ width: 28, display: "flex", justifyContent: "center" }}
                >
                  {col.locked ? (
                    <Tooltip title="Mandatory field">
                      <LockIcon
                        sx={{
                          fontSize: 15,
                          color: "primary.main",
                          opacity: 0.6,
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <DragIndicatorIcon
                      sx={{
                        fontSize: 18,
                        color: "rgba(74,222,128,0.7)",
                        cursor: "grab",
                      }}
                    />
                  )}
                </Box>

                <TextField
                  size="small"
                  sx={{ flex: 2 }}
                  value={col.name}
                  onChange={(e) => updateColumn(col.id, "name", e.target.value)}
                  placeholder="column_name"
                  disabled={col.locked}
                  error={!col.locked && col.name.trim() === ""}
                  helperText={
                    !col.locked && col.name.trim() === "" ? "Required" : ""
                  }
                  inputProps={{
                    style: { fontFamily: "'DM Mono', monospace", fontSize: 13 },
                  }}
                />

                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={col.type}
                    onChange={(e) =>
                      updateColumn(col.id, "type", e.target.value)
                    }
                    disabled={col.locked}
                    sx={{ fontSize: 13, fontFamily: "'DM Mono', monospace" }}
                  >
                    {COLUMN_TYPES.map((t) => (
                      <MenuItem
                        key={t}
                        value={t}
                        sx={{
                          fontSize: 13,
                          fontFamily: "'DM Mono', monospace",
                        }}
                      >
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box
                  sx={{ width: 80, display: "flex", justifyContent: "center" }}
                >
                  {col.locked ? (
                    <Chip
                      label="always"
                      size="small"
                      sx={{
                        fontSize: 10,
                        bgcolor: "rgba(74,222,128,0.12)",
                        color: "primary.main",
                        height: 20,
                      }}
                    />
                  ) : (
                    <Switch
                      checked={col.required}
                      onChange={(e) =>
                        updateColumn(col.id, "required", e.target.checked)
                      }
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "primary.main",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          { bgcolor: "primary.main" },
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{ width: 36, display: "flex", justifyContent: "center" }}
                >
                  {!col.locked && (
                    <IconButton
                      size="small"
                      onClick={() => removeColumn(col.id)}
                      sx={{
                        color: "rgba(74,222,128,0.7)",
                        "&:hover": {
                          color: "#f87171",
                          bgcolor: "rgba(248,113,113,0.08)",
                        },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Fade>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={addColumn}
            variant="outlined"
            fullWidth
            sx={{
              mt: 1.5,
              borderColor: "rgba(74,222,128,0.25)",
              color: "text.secondary",
              borderStyle: "dashed",
              py: 1,
              fontFamily: "'DM Mono', monospace",
              fontSize: 13,
              "&:hover": {
                borderColor: "primary.main",
                color: "primary.main",
                bgcolor: "rgba(74,222,128,0.04)",
              },
            }}
          >
            Add Column
          </Button>
        </Paper>

        {/* Schema preview */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: "rgba(74,222,128,0.03)" }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              letterSpacing: 1,
              textTransform: "uppercase",
              mb: 2,
              display: "block",
            }}
          >
            Schema Preview
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {columns.map((col) => (
              <Chip
                key={col.id}
                label={
                  <Box
                    component="span"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <span>{col.name || "unnamed"}</span>
                    <Box component="span" sx={{ opacity: 0.5, fontSize: 10 }}>
                      · {col.type}
                    </Box>
                    {col.required && (
                      <Box component="span" sx={{ color: "primary.main" }}>
                        *
                      </Box>
                    )}
                  </Box>
                }
                size="small"
                sx={{
                  bgcolor: col.locked
                    ? "rgba(74,222,128,0.1)"
                    : "rgba(74,222,128,0.06)",
                  border: "1px solid rgba(74,222,128,0.25)",
                  color: col.locked ? "primary.main" : "text.secondary",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/sheet-schemas")}
            sx={{
              borderColor: "rgba(74,222,128,0.25)",
              color: "text.secondary",
            }}
          >
            Cancel
          </Button>
          <GlassButtonLoading
            variant="contained"
            fullWidth
            disabled={!allValid || !sheetName.trim() || saving}
            onClick={handleSave}
            startIcon={<SaveIcon />}
          >
            {saving ? "Saving…" : "Save Changes"}
          </GlassButtonLoading>
        </Box>

        {!allValid && (
          <Alert
            severity="warning"
            sx={{
              mt: 2,
              bgcolor: "rgba(234,179,8,0.08)",
              border: "1px solid rgba(234,179,8,0.2)",
              color: "#fbbf24",
              fontFamily: "'DM Mono', monospace",
              fontSize: 12,
            }}
          >
            All column names must be filled before saving.
          </Alert>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          severity={snackbar.severity}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
